import type { PhaseActionWidget } from "@/lib/types";
import type { ReactNode } from "react";
import { Button } from "@/components/ui";
import { PHASE_WIDGET_CATALOG } from "@/lib/data/static/phase-widget-catalog";
import { Clock3, Pencil } from "lucide-react";

const SAMPLE_FIELDS: Partial<
  Record<PhaseActionWidget["type"], { label: string; value: string }[]>
> = {
  LiabilityCoverage: [
    { label: "Incident report", value: "Received" },
    { label: "Policy limits", value: "$100,000" },
    { label: "Third-party coverage", value: "Confirmed" },
    { label: "Liability acceptance", value: "Pending" },
  ],
  TreatmentSummary: [
    { label: "Total bills", value: "$18,450" },
    { label: "Paid", value: "$7,200" },
    { label: "Outstanding", value: "$11,250" },
  ],
  SentDemands: [
    { label: "Sent demand", value: "Oct 8" },
    { label: "Amount", value: "$75,000" },
    { label: "Expiration", value: "Nov 7" },
  ],
  Coverage: [
    { label: "Third-party coverage", value: "$100,000" },
    { label: "First-party coverage", value: "$50,000" },
    { label: "Policy limits", value: "Confirmed" },
  ],
  Providers: [
    { label: "Provider", value: "Westside Physiotherapy" },
    { label: "Records", value: "Requested" },
    { label: "Outstanding balance", value: "$3,250" },
  ],
  SettlementSummary: [
    { label: "Final settlement", value: "$75,000" },
    { label: "Total fees", value: "$25,000.00" },
    { label: "Total costs", value: "$4,200" },
    { label: "Client net", value: "$45,800" },
  ],
  CycleTimeSummary: [
    { label: "Time on desk", value: "12 days" },
    { label: "Matter status duration", value: "34 days" },
  ],
  FinalLienResolutions: [
    { label: "Lienholder", value: "Health plan" },
    { label: "Original amount", value: "$12,000" },
    { label: "Final amount", value: "$8,500" },
  ],
};

export function PhaseWidgetPreview({
  widget,
  actions,
  onEdit,
  headerLeading,
  headerTrailing,
  groupActionControls,
  showSampleData = true,
}: {
  widget: PhaseActionWidget;
  actions: { id: string; title: string }[];
  onEdit?: () => void;
  headerLeading?: ReactNode;
  headerTrailing?: ReactNode;
  groupActionControls?: (actionId: string) => ReactNode;
  showSampleData?: boolean;
}) {
  const wide = PHASE_WIDGET_CATALOG.find(
    (entry) => entry.type === widget.type,
  )?.wide;
  const fields = SAMPLE_FIELDS[widget.type];
  const sampleElapsedDays =
    widget.type === "Countdown" ? Math.min(12, widget.durationDays) : 0;
  const isSummary =
    widget.type === "TreatmentSummary" ||
    widget.type === "SettlementSummary" ||
    widget.type === "CycleTimeSummary";

  return (
    <section
      aria-label={`Preview of ${widget.title || "Untitled widget"}`}
      className={`border-border/70 bg-card h-full min-w-0 overflow-hidden rounded-2xl border p-5 ${wide ? "@min-[760px]:col-span-full" : ""}`}
    >
      <div className="border-border/70 flex items-start justify-between gap-3 border-b pb-4">
        <div className="flex min-w-0 items-start gap-2">
          {headerLeading}
          <h3 className="min-w-0 text-sm font-semibold break-words">
            {widget.title || "Untitled widget"}
          </h3>
        </div>
        {headerTrailing}
        {onEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={onEdit}
            aria-label={`Edit ${widget.title || "untitled widget"}`}
            title={`Edit ${widget.title || "untitled widget"}`}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {widget.type === "Countdown" ? (
        <div className="flex min-h-44 flex-col justify-between gap-6 pt-5">
          <div>
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Clock3 className="size-5 shrink-0" aria-hidden="true" />
              {showSampleData
                ? `${Math.max(widget.durationDays - sampleElapsedDays, 0)} days remaining`
                : `${widget.durationDays} day countdown`}
            </div>
            <p className="text-muted-foreground mt-2 text-sm">
              {showSampleData
                ? `Day ${sampleElapsedDays} of ${widget.durationDays} in this phase`
                : "Starts on first progress"}
            </p>
          </div>
          <div>
            <div className="bg-muted h-2 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full bg-amber-400"
                style={{
                  width: `${showSampleData && widget.durationDays > 0 ? (sampleElapsedDays / widget.durationDays) * 100 : 0}%`,
                }}
              />
            </div>
            <div className="text-muted-foreground mt-2 flex justify-between text-xs">
              <span>Day 0</span>
              <span>Day {widget.durationDays}</span>
            </div>
          </div>
        </div>
      ) : widget.type === "ActionGroup" ? (
        <div className="divide-border/70 divide-y">
          {widget.actionDefinitionIds.length > 0 ? (
            widget.actionDefinitionIds.map((id) => (
              <div key={id} className="flex items-center gap-3 py-3 text-sm">
                <span className="border-border size-4 shrink-0 rounded border" />
                <span className="min-w-0 flex-1 break-words">
                  {actions.find((action) => action.id === id)?.title ??
                    "Unavailable Action"}
                </span>
                {groupActionControls?.(id)}
              </div>
            ))
          ) : (
            <p className="text-muted-foreground py-4 text-sm">
              Add actions to see them grouped here.
            </p>
          )}
        </div>
      ) : widget.type === "TreatmentTimeline" ? (
        <div className="space-y-3 pt-5">
          {showSampleData ? (
            [
              { label: "Initial assessment", time: "Week 1" },
              { label: "Follow-up appointment", time: "Week 2" },
            ].map(({ label, time }) => (
              <div key={label} className="flex items-center gap-3 text-sm">
                <span className="bg-primary size-2 shrink-0 rounded-full" />
                <span className="text-muted-foreground">{label}</span>
                <span className="text-muted-foreground ml-auto">{time}</span>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">
              Treatment events will appear here.
            </p>
          )}
        </div>
      ) : isSummary ? (
        <div className="mt-5 overflow-hidden rounded-xl border">
          <div
            className={`grid grid-cols-2 ${widget.type === "SettlementSummary" ? "lg:grid-cols-4" : widget.type === "TreatmentSummary" ? "lg:grid-cols-3" : ""}`}
          >
            {fields?.map(({ label, value }) => (
              <div
                key={label}
                className="border-border/70 border-r p-4 last:border-r-0"
              >
                <p className="text-muted-foreground text-xs">{label}</p>
                <p className="mt-2 text-xl font-semibold">
                  {showSampleData ? value : "—"}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="divide-border/70 divide-y pt-2">
          {fields?.map(({ label, value }) => (
            <div
              key={label}
              className="flex items-center justify-between gap-3 py-3 text-sm"
            >
              <span className="text-muted-foreground">{label}</span>
              <span className="text-foreground text-right">
                {showSampleData ? value : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
